import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { BackendApiService } from '../../../../services/backend-api.service';

export interface NoteHistoryItem {
  id: string;
  action: string;
  user: string;
  timestamp: Date | string;
  details?: string;
}

/**
 * NoteHistoryTabComponent
 *
 * Display component for note activity history/timeline.
 * Shows chronological list of changes, comments, status updates, etc.
 * Uses the unified activity/log API endpoint (same as tickets).
 *
 * @selector app-note-history-tab
 * @changeDetection OnPush
 */
@Component({
    selector: 'app-note-history-tab',
    templateUrl: './note-history-tab.component.html',
    styleUrls: ['./note-history-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NoteHistoryTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() noteId: string | number = '';
  @Input() historyData: NoteHistoryItem[] = [];
  @Input() noteModel: any;
  @Output() historyChange = new EventEmitter<any[]>();

  private readonly NOTE_MODULE_ID = 23;
  private readonly subscription = new Subscription();
  private hasInitialized = false;

  requestIsInProgress = false;
  showLoadMoreOption = false;
  activityLogMeta: any;
  param = { per_page: 5, page: 1 };
  search = '';

  constructor(
    private backendApiService: BackendApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.tryLoadHistory(true);
    this.hasInitialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasInitialized && !(changes['noteId'] || changes['noteModel'])) {
      return;
    }

    if (changes['noteId'] || changes['noteModel']) {
      this.resetPagination();
      this.tryLoadHistory(true);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadMore(): void {
    if (this.requestIsInProgress || !this.showLoadMoreOption) {
      return;
    }
    this.param.page += 1;
    this.tryLoadHistory(false);
  }

  getEventIcon(action: string): string {
    const icons: Record<string, string> = {
      created: 'add_circle',
      assigned: 'person_add',
      checklist: 'check_circle',
      milestone: 'flag',
      status: 'swap_horiz',
      status_changed: 'swap_horiz',
      comment: 'comment',
      commented: 'comment',
      attachment: 'attach_file',
      file_uploaded: 'attach_file',
      completed: 'done_all',
      updated: 'edit',
      deleted: 'delete',
      flagged: 'flag',
      unflagged: 'outlined_flag',
    };
    return icons[action?.toLowerCase()] || 'info';
  }

  getEventIconClass(action: string): string {
    const classMap: { [key: string]: string } = {
      created: 'event-icon-created',
      assigned: 'event-icon-assigned',
      checklist: 'event-icon-checklist',
      milestone: 'event-icon-milestone',
      status: 'event-icon-status',
      status_changed: 'event-icon-status_changed',
      comment: 'event-icon-comment',
      commented: 'event-icon-commented',
      attachment: 'event-icon-attachment',
      file_uploaded: 'event-icon-file_uploaded',
      completed: 'event-icon-completed',
      updated: 'event-icon-updated',
      deleted: 'event-icon-default',
      flagged: 'event-icon-flagged',
      unflagged: 'event-icon-unflagged',
    };
    return classMap[action?.toLowerCase()] || 'event-icon-default';
  }

  formatEventTime(timestamp: Date | string): string {
    const date =
      typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) {
      return 'Just now';
    }
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    }
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    }
    if (diffDays < 30) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }

  formatTimestamp(timestamp: Date | string): string {
    const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private tryLoadHistory(reset: boolean): void {
    if (!this.noteId || this.requestIsInProgress) {
      return;
    }

    const subjectId = this.resolveSubjectId();
    if (!subjectId) {
      console.warn(
        '[NOTE-HISTORY] Missing note reference id, cannot load history.'
      );
      return;
    }

    if (reset) {
      this.historyData = [];
      this.activityLogMeta = null;
      this.showLoadMoreOption = false;
      this.param.page = 1;
    }

    const queryParams = new URLSearchParams({
      module_id: this.NOTE_MODULE_ID.toString(),
      subject_id: subjectId,
      sort_by: 'id',
      sort_order: 'DESC',
      per_page: this.param.per_page.toString(),
      page: this.param.page.toString(),
      search: this.search,
    });

    this.requestIsInProgress = true;
    const sub = this.backendApiService
      .getApi(`activity/log?${queryParams.toString()}`)
      .subscribe({
        next: (response: any) => {
          const newEntries = this.transformActivityLog(response?.data || []);
          this.historyData = reset
            ? newEntries
            : [...(this.historyData || []), ...newEntries];
          this.historyChange.emit(this.historyData);
          this.activityLogMeta = response?.meta;
          this.updateLoadMoreOption();
          this.requestIsInProgress = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('[NOTE-HISTORY] Failed to load activity log:', err);
          this.requestIsInProgress = false;
          this.cdr.markForCheck();
        },
      });

    this.subscription.add(sub);
  }

  private resolveSubjectId(): string | null {
    if (this.noteModel?.id) {
      return String(this.noteModel.id);
    }
    if (this.noteId) {
      return String(this.noteId);
    }
    if (this.noteModel?.reference?.id) {
      return String(this.noteModel.reference.id);
    }
    if (this.noteModel?.reference_id) {
      return String(this.noteModel.reference_id);
    }
    return null;
  }

  private transformActivityLog(logs: any[]): NoteHistoryItem[] {
    return logs.map((log: any) => {
      const type = log.event || log.log_name || log.activity_type || 'updated';
      return {
        id: log.Activity_id?.toString() || log.id?.toString() || '',
        action: type,
        user:
          log.perform_by?.full_name ||
          log.perform_by?.name ||
          log.causer?.full_name ||
          log.user?.full_name ||
          log.perform_by ||
          log.causer ||
          log.user ||
          'Unknown User',
        timestamp: log.created_at_raw || log.created_at || log.timestamp || new Date().toISOString(),
        details:
          log.details ||
          (log.description ? this.stripHtml(log.description) : ''),
      };
    });
  }

  private updateLoadMoreOption(): void {
    this.showLoadMoreOption =
      this.activityLogMeta &&
      this.activityLogMeta.current_page < this.activityLogMeta.last_page;
  }

  private resetPagination(): void {
    this.param = { per_page: 5, page: 1 };
  }

  formatAction(action: string): string {
    if (!action) {
      return 'updated note';
    }
    const normalized = action.toLowerCase();
    const actionMap: { [key: string]: string } = {
      created: 'created this note',
      updated: 'updated note',
      status_changed: 'changed status',
      assigned: 'assigned note',
      commented: 'commented',
      attached: 'added attachment',
      completed: 'completed note',
      deleted: 'deleted note',
      flagged: 'flagged note',
      unflagged: 'removed flag',
    };
    if (actionMap[normalized]) {
      return actionMap[normalized];
    }

    return normalized
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }
}
